import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGame } from './state/useGame.js'
import { loadRoster, fetchStory, takeStory, prefetchStory } from './lib/api.js'
import { pickSurprise } from './lib/surprise.js'
import { buildPreview, withStoryEdges } from './lib/preview.js'
import { stopSpeaking } from './lib/speech.js'
import { installReadAloudKey } from './lib/hotkey.js'
import { installTapRescue } from './lib/tap.js'
import { indexSounds } from './lib/sounds.js'
import { prefetchClips, spokenLines } from './lib/voice_api.js'
import { track, trackScreen, install as installAnalytics, setAnalyticsContext } from './lib/analytics.js'
import { parseMatchup } from '../server/match_slug.js'
import { pendingLink, isBattleLink } from './lib/share.js'
import TopBar from './components/TopBar.jsx'
import { indexArt } from './components/AnimalImage.jsx'
import AgePicker from './screens/AgePicker.jsx'
import Roster from './screens/Roster.jsx'
import Rumble, { RUMBLE_MIN_MS } from './screens/Rumble.jsx'
import Matchup from './screens/Matchup.jsx'
import Facts from './screens/Facts.jsx'
import Battle from './screens/Battle.jsx'
import Reveal from './screens/Reveal.jsx'
import Quiz from './screens/Quiz.jsx'
import Collection from './screens/Collection.jsx'
import Credits from './screens/Credits.jsx'

/**
 * Screen order (SPEC Section 7):
 * age -> roster -> rumble -> matchup -> facts -> [rumble] -> battle -> reveal -> quiz -> roster
 * The collection is reachable from the top bar at any time, and the photo
 * credits from the bottom of the collection (D-133).
 *
 * The three story screens step backwards as well as forwards (D-127): Back on
 * the battle's first beat returns to the fact page the kid left — `factsPage`
 * lives in the reducer for exactly that — and Back on the first fact page
 * returns to the matchup. No Back is ever a greyed-out dead end.
 *
 * Loading (D-099): tapping Fight! shows the one interstitial — "Let's get ready
 * to rumble!", about two seconds — while the matchup and every fact page are
 * built from roster.json and the model starts on the battle behind them. The
 * story is needed only at the battle door; if it is still being written when
 * the kid gets there, the same interstitial covers the rest of the wait and
 * steps aside the moment it lands. No button is ever disabled while waiting.
 * Speech is parsed on the device with the matcher the server uses, so "Say it"
 * joins this path instead of waiting on a round trip.
 *
 * Usage telemetry (docs/analytics.md) is wired here as a bystander: every
 * `track()` is fire-and-forget and nothing a kid does waits on it.
 *
 * A shared battle link (D-145, docs/SHARE.md) — /battle/<a>/<b>?age=<band> —
 * is consumed once the roster is here: the address is cleaned back to `/` and
 * the pair goes through the same `startBattle` as a tap on Fight!, so the
 * recipient lands on the rumble, then the matchup, and still gets to make the
 * call. A link that names one animal pre-picks it; one that names nothing the
 * roster knows opens the roster, silently — a kid or a grandparent can do
 * nothing about a bad address, and the roster is a good place to be.
 */
export default function App() {
  const [game, dispatch] = useGame()
  const [roster, setRoster] = useState(null)
  const [rosterError, setRosterError] = useState(null)

  useEffect(() => {
    installAnalytics()
    installReadAloudKey() // the space bar reads the screen aloud (D-112)
    installTapRescue() // a tap a small finger slid too far still counts (D-161)
    loadRoster()
      .then((r) => {
        indexArt(r.animals) // before the first card renders, so no image asks for a file that is not there
        indexSounds(r.sounds) // which sound tags have a real file (D-088)
        setAnalyticsContext({ version: r.roster_hash })
        setRoster(r)
      })
      .catch(() => {
        track('app_error', { kind: 'roster' })
        setRosterError('Could not load the animals. Are you online?')
      })
  }, [])

  // Silence any read-aloud whenever the screen changes.
  useEffect(() => stopSpeaking, [game.screen])
  useEffect(() => trackScreen(game.screen), [game.screen])
  useEffect(() => setAnalyticsContext({ age: game.age }), [game.age])

  /**
   * Start the story the moment the second animal is picked (D-086). A live
   * model takes 6–24s; by the time a kid has looked at the two cards and found
   * the Fight! button the story is usually here, and the request is shared with
   * the one Fight! makes, so nothing is generated twice. The short delay skips
   * a pair the kid immediately changes.
   */
  useEffect(() => {
    if (game.screen !== 'roster' || !game.a || !game.b || game.story) return
    const t = setTimeout(() => prefetchStory({ a: game.a, b: game.b, age: game.age }), 350)
    return () => clearTimeout(t)
  }, [game.screen, game.a, game.b, game.age, game.story])

  /**
   * The interstitial's timing (D-099). "ready" holds for its minimum and then
   * shows the matchup, which needs nothing from the network. "battle" holds
   * until the story is here AND its minimum has passed, so a story that lands
   * 50ms after the kid arrives does not flash the screen.
   */
  useEffect(() => {
    if (game.screen !== 'rumble') return undefined
    if (game.rumble === 'battle' && !game.story) return undefined
    const wait = Math.max(0, RUMBLE_MIN_MS[game.rumble] - (Date.now() - game.rumbleAt))
    const t = setTimeout(() => dispatch({ type: 'go', screen: game.rumble === 'ready' ? 'matchup' : 'battle' }), wait)
    return () => clearTimeout(t)
  }, [game.screen, game.rumble, game.story, game.rumbleAt, dispatch])

  const bySlug = useMemo(() => new Map((roster?.animals || []).map((x) => [x.slug, x])), [roster])
  const A = game.a ? bySlug.get(game.a) : null
  const B = game.b ? bySlug.get(game.b) : null

  const go = (screen) => dispatch({ type: 'go', screen })

  // The story request runs behind the screens, so backing out has to cancel it.
  const inFlight = useRef(null)

  /** Ask for the story and land it in state. Joins a prefetch if one is running. */
  const requestStory = useCallback(
    async ({ a, b }) => {
      const t0 = performance.now()
      inFlight.current?.abort()
      const ctl = new AbortController()
      inFlight.current = ctl
      try {
        const res = await fetchStory({ a, b, age: game.age, signal: ctl.signal })
        if (ctl.signal.aborted) return
        dispatch({ type: 'story', story: res.story, numbers: res.numbers, fromCache: res.fromCache })
        track('story_ready', {
          a,
          b,
          age: game.age,
          ms: Math.round(performance.now() - t0),
          source: res.fromCache ? 'device' : 'server',
          fallback: !!res.meta?.fallback,
        })
        // Warm the later lines while the kid is on the matchup and facts screens,
        // so the battle beats never wait on the network. Best effort: a failure
        // just means those lines are read with the OS voice (D-080).
        void prefetchClips(spokenLines(res.story))
      } catch (err) {
        // An abandoned story is not a failure and is never reported as one.
        if (ctl.signal.aborted || err.name === 'AbortError') return
        track('story_error', { status: Number.isInteger(err.status) ? err.status : 0 })
        // Surfaces at the battle door, where it blocks; the matchup and facts
        // screens need nothing from the network and show as normal.
        dispatch({ type: 'error', error: err.message || 'Could not build that story.' })
      }
    },
    [game.age, dispatch],
  )

  /** Fight!: the interstitial, the matchup and facts from the roster, the story behind them. */
  const startBattle = useCallback(
    ({ a, b, heard = null }) => {
      track('story_requested', { a, b, age: game.age })
      const preview = buildPreview({ a: bySlug.get(a), b: bySlug.get(b), age: game.age, roster })
      dispatch({ type: 'start', a, b, heard, preview, now: Date.now() })

      // A pair this device has already played needs no request — and since
      // D-086 that includes a pair whose prefetch finished while the kid was
      // still looking at the picks. `takeStory` returns the story and the
      // decision arithmetic beside it, never the story alone — and marks a
      // rolled fight as played, so the next time is a fresh roll (D-157).
      const cached = takeStory(a, b, game.age)
      if (cached) {
        inFlight.current?.abort()
        dispatch({ type: 'story', story: cached.story, numbers: cached.numbers, fromCache: true })
        track('story_ready', { a, b, age: game.age, ms: 0, source: 'device', fallback: false })
        void prefetchClips(spokenLines(cached.story))
        return
      }
      requestStory({ a, b })
    },
    [game.age, dispatch, roster, bySlug, requestStory],
  )

  /**
   * The shared battle link this page opened with, if any (D-145). Runs once the
   * roster is loaded and never again: the ref survives StrictMode's replayed
   * effects, and the address is cleaned first so a reload, Home and the next
   * launch of an installed app are all ordinary first runs. The slugs are
   * matched by exact membership only — a link is machine-written, so the speech
   * and search matchers (D-075) have no business here — and nothing from the
   * address is ever rendered.
   */
  const linkConsumed = useRef(false)
  useEffect(() => {
    if (!roster || !pendingLink || linkConsumed.current) return
    linkConsumed.current = true
    try {
      history.replaceState(null, '', '/')
    } catch {
      /* an address we cannot clean is still a battle we can play */
    }
    const a = pendingLink.a && bySlug.has(pendingLink.a) ? pendingLink.a : null
    const b = pendingLink.b && bySlug.has(pendingLink.b) ? pendingLink.b : null
    if (a && b && a !== b && isBattleLink(pendingLink)) {
      track('shared_open', { a, b, age: game.age })
      startBattle({ a, b })
      return
    }
    // One animal, or nothing: the roster, and the device's own band (the
    // reducer restores it with the pair). `age` alone is the bad-link signal.
    track('shared_open', { age: game.age })
    const one = a || b
    dispatch(one ? { type: 'setPair', a: one, b: null } : { type: 'clearPair' })
  }, [roster, bySlug, startBattle, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

  /** "Say it": the same matcher the server uses for the audio box, run on the device. */
  const onSpeech = (text) => {
    const parsed = parseMatchup(text, roster.animals)
    if (!parsed.ok) {
      track('speech_result', { ok: false })
      dispatch({ type: 'error', error: `Sorry, I ${parsed.error}.` }) // the roster shows it as a toast
      return
    }
    track('speech_result', { ok: true })
    startBattle({ a: parsed.a, b: parsed.b, heard: { text: parsed.heard, matches: parsed.matches } })
  }

  /** The battle door. */
  const toBattle = () => {
    track('facts_done', { pages: game.preview?.fact_pages.length || 0 })
    dispatch({ type: 'toBattle', now: Date.now() })
  }

  const retryStory = () => {
    dispatch({ type: 'retry', now: Date.now() })
    requestStory({ a: game.a, b: game.b })
  }

  // Leaving a matchup abandons the story being written for it.
  const leaveStory = useCallback((action) => {
    inFlight.current?.abort()
    inFlight.current = null
    dispatch(action)
  }, [dispatch])

  /** Surprise me: loaded dice, a fight worth watching (D-158, src/lib/surprise.js). */
  const surprise = () => {
    const [x, y] = pickSurprise(roster)
    track('surprise')
    dispatch({ type: 'setPair', a: x, b: y })
  }

  /** Mirrors the `pick` reducer so the event says which slot the tap filled. */
  const pick = (slug) => {
    if (game.a === slug || game.b === slug) track('unpick', { slug })
    else track('pick', { slug, side: game.a && !game.b ? 'b' : 'a' })
    dispatch({ type: 'pick', slug })
  }

  /**
   * Pre-pick one animal and go find it a challenger (the collection's "fight
   * again", and the reveal's "Keep"). Both leave a matchup behind, so this goes
   * through leaveStory to abandon any story still being written for it.
   */
  const keep = (slug) => {
    leaveStory({ type: 'setPair', a: slug, b: null })
    go('roster')
  }

  // The matchup shows the preview with the story's combat-score edges filled in
  // as they arrive. Every cell keeps the preview's wording, so no text rewrites
  // itself under a kid who is already reading it (D-071). Memoised: the Matchup
  // staggers its stat rows in once per story object, and a render that changes
  // nothing about the story (a call, D-135) must not replay that.
  const matchupStory = useMemo(() => (game.preview ? withStoryEdges(game.preview, game.story) : game.story), [game.preview, game.story])

  if (rosterError) return <main className="app"><p className="fatal">{rosterError}</p></main>
  if (!roster) return <main className="app"><p className="fatal">Loading…</p></main>

  const factPages = game.preview?.fact_pages || game.story?.fact_pages

  // battle/reveal/quiz all need the finished story. The battle door never lets
  // a kid past without one, but a stale screen after an error must not render
  // against a null story: the interstitial covers it, with retry.
  const needsStory = ['battle', 'reveal', 'quiz'].includes(game.screen)

  let screen
  switch (needsStory && !game.story ? 'rumble' : game.screen) {
    case 'age':
      screen = <AgePicker current={game.age} onPick={(age) => { track('age_set', { age }); dispatch({ type: 'setAge', age }) }} onBack={() => go('roster')} />
      break
    case 'roster':
      screen = (
        // Keyed on the visit (D-163): Home is a fresh start, so the search text
        // and the category tab go with it even when Home is tapped from the
        // roster itself and the screen would otherwise never be rebuilt.
        <Roster
          key={game.visit}
          roster={roster}
          age={game.age}
          a={game.a}
          b={game.b}
          heard={game.heard}
          onPick={pick}
          onSurprise={surprise}
          onSpeech={onSpeech}
          onClearHeard={() => dispatch({ type: 'clearPair' })}
          onFight={() => startBattle({ a: game.a, b: game.b })}
        />
      )
      if (game.status === 'error' && game.error && !game.heard) {
        screen = <>{screen}<p className="toast" role="alert">{game.error}</p></>
      }
      break
    case 'rumble':
      screen = (
        <Rumble
          A={A}
          B={B}
          age={game.age}
          phase={game.screen === 'rumble' ? game.rumble : 'battle'}
          error={game.status === 'error' ? game.error : null}
          onRetry={retryStory}
          onBack={() => { leaveStory({ type: 'clearPair' }); go('roster') }}
        />
      )
      break
    case 'matchup':
      // The call (D-135): tap an animal in the scene to say who wins. Off at age 2.
      screen = <Matchup story={matchupStory} A={A} B={B} age={game.age} call={game.call} onCall={game.age === '2' ? null : (slug) => dispatch({ type: 'call', slug })} onFight={() => { track('fight'); go('facts') }} />
      break
    case 'facts':
      screen = (
        <Facts
          pages={factPages}
          vocab={game.story?.vocab_word}
          A={A}
          B={B}
          age={game.age}
          page={Math.min(game.factsPage, factPages.length - 1)}
          onPage={(n) => dispatch({ type: 'factsPage', n })}
          onBack={() => go('matchup')}
          onDone={toBattle}
        />
      )
      break
    case 'battle':
      screen = <Battle story={game.story} A={A} B={B} age={game.age} features={roster.features} onBack={() => go('facts')} onDone={() => { track('battle_done', { beats: game.story.battle.length }); go('reveal') }} />
      break
    case 'reveal':
      screen = <Reveal story={game.story} numbers={game.numbers} A={A} B={B} age={game.age} call={game.call} features={roster.features} onQuiz={() => go('quiz')} onAgain={() => dispatch({ type: 'reset' })} onKeep={keep} />
      break
    case 'quiz':
      screen = <Quiz story={game.story} A={A} B={B} age={game.age} onDone={() => dispatch({ type: 'reset' })} />
      break
    case 'collection':
      screen = <Collection roster={roster} onBack={() => go('roster')} onRematch={(slug) => { track('rematch', { slug }); keep(slug) }} onCredits={() => go('credits')} />
      break
    case 'credits':
      screen = <Credits onBack={() => go('collection')} />
      break
    default:
      screen = null
  }

  return (
    <main className={`app app--${game.screen}`}>
      <TopBar age={game.age} screen={game.screen} onAge={() => go('age')} onHome={() => leaveStory({ type: 'reset' })} onCollection={() => go('collection')} />
      {screen}
    </main>
  )
}
